// @flow
// ----------------------------------------------------------------------
// <copyright file="Izenda.GoogleMapsVizEngine.js" company="Izenda">
//  Copyright (c) 2015 Izenda, Inc.
//  ALL RIGHTS RESERVED
//
//  The entire contents of this file is protected by U.S. and
//  International Copyright Laws. Unauthorized reproduction,
//  reverse-engineering, and distribution of all or any portion of
//  the code contained in this file is strictly prohibited and may
//  result in severe civil and criminal penalties and will be
//  prosecuted to the maximum extent possible under the law.
//
//  RESTRICTIONS
//
//  THIS SOURCE CODE AND ALL RESULTING INTERMEDIATE FILES
//  ARE CONFIDENTIAL AND PROPRIETARY TRADE
//  SECRETS OF IZENDA INC. THE REGISTERED DEVELOPER IS
//  LICENSED TO DISTRIBUTE THE PRODUCT AND ALL ACCOMPANYING .NET
//  CONTROLS AS PART OF AN EXECUTABLE PROGRAM ONLY.
//
//  THE SOURCE CODE CONTAINED WITHIN THIS FILE AND ALL RELATED
//  FILES OR ANY PORTION OF ITS CONTENTS SHALL AT NO TIME BE
//  COPIED, TRANSFERRED, SOLD, DISTRIBUTED, OR OTHERWISE MADE
//  AVAILABLE TO OTHER INDIVIDUALS WITHOUT EXPRESS WRITTEN CONSENT
//  AND PERMISSION FROM IZENDA INC.
//
//  CONSULT THE END USER LICENSE AGREEMENT(EULA FOR INFORMATION ON
//  ADDITIONAL RESTRICTIONS.
//  </copyright>
// ----------------------------------------------------------------------

// View of code

// On Izenda page: viewer, designer
// Each map has its own google maps API (google object,...)
// map is isolated inside an iframe.
// map is destroyed with/by unloading iframe
// map required izenda-ui.css

// On export
// dont render google map into iframe
// use script cache

import ReportPartMapConstants from 'logic/data/Izenda.ReportPartMapConstants';
import { isEmpty, find, isNil, first, filter } from 'lodash/fp';
import { GoogleApi } from './google_api_loader/Izenda.GoogleApi';
import VizEngine from '../Izenda.VizEngine';
import uiState from 'logic/Izenda.UiState';
import izendaCfg from 'Izenda.Config';
import GoogleMapsDrawingService from './Izenda.GoogleMapsDrawingService';
import { ScriptCache } from './google_api_loader/Izenda.ScriptCache';
import GMPAGE from '../../../../../static/html/gm_page.html';

const GOOGLE_MAP_ZOOM_SIZE_TILE = 256;
const onCompletedEventName = 'idle';
const MIN_CONTAINER_SIZE = 15;


export default class GoogleMapsVizEngine extends VizEngine {
  constructor(...args) {
    super(...args);

    this.shouldRender = true;
    this.chartContainer = null;
    this.drawingService = new GoogleMapsDrawingService();
    this.options = {};
    this.iframe = null;
    this.googleApiUrl = GoogleApi({
      apiKey: isEmpty(uiState.googleApiKey) ? 'invalid key' : uiState.googleApiKey,
      v: izendaCfg.GoogleMapJSApiVersion
    });
    this.izendaUICssLink = this.resolveIzendaUICSS();
  }

  /**
   * Draw
   * @param {DOM} chartContainer
   * @param {string} chartType
   * @param {Object} options
   * @param {Function} onCompleted
   */
  draw(chartContainer, chartType, options, onCompleted) {
    this.chartContainer = chartContainer;
    if (!this.shouldRender) {
      return;
    }

    const { offsetWidth: containerWidth, offsetHeight: containerHeight } = this.chartContainer;
    if (Math.min(containerWidth, containerHeight) <= MIN_CONTAINER_SIZE) {
      return this.drawingService.drawIcon(this.chartContainer);
    }

    return this.drawMap(options, onCompleted);
  }

  /**
   * Draw map
   * @param {Object} options
   * @param {Function} onCompleted
   */
  drawMap(options: Object, onCompleted: Function) {
    this.options = options;
    if (this.options.printingMode) {
      // callback when google api key is invalid
      window.gm_authFailure = this.onAuthFailure.bind(this);
      this.drawToCurrentDocument(options, onCompleted);
    } else {
      this.drawToIFrame(options, onCompleted);
    }

    return {
      destroy: this.clearAll
    };
  }

  /**
   * Clear data
   */
  clearAll = () => {
    this.clearIFrame(this.iframe);
    delete this.drawingService;
    delete this.options;
    delete this.iframe;
    delete this.chartContainer;
  };

  /**
   * Draw google map on current page
   * Intended to use on export, because callback function, syncfusion, and .net core cannot work together well.
   * @param {Object} options
   * @param {Function} onCompleted
   */
  drawToCurrentDocument(options, onCompleted) {
    const head = first(document.getElementsByTagName('head'));
    const insertBefore = head.insertBefore;
    head.insertBefore = (newElement, referenceElement) => {
      // Fix conflict Roboto font using google map and izenda
      // We only load and use Roboto of izenda.
      if (newElement.href && newElement.href.indexOf('//fonts.googleapis.com/css?family=Roboto') > -1) {
        return;
      }
      insertBefore.call(head, newElement, referenceElement);
    };

    const host = this;
    const defaultCreateCache = () => {
      return ScriptCache(window, {
        izGoogle: GoogleApi({
          apiKey: isEmpty(uiState.googleApiKey) ? 'invalid key' : uiState.googleApiKey,
          v: izendaCfg.GoogleMapJSApiVersion
        })
      });
    };

    this.scriptCache = this.scriptCache ? this.scriptCache : defaultCreateCache();

    if (!window.google) {
      const onLoad = err => {
        if (!!err) {
          this.drawingService.drawFailing(this.chartContainer);
        } else {
          this.google = window.google;
          this.drawProcess.call(this, host, this.chartContainer, options, onCompleted);
        }
      };
      return this.scriptCache.izGoogle.onLoad(onLoad);
    }
    this.google = window.google;
    this.drawProcess.call(this, host, this.chartContainer, options, onCompleted);
  }

  /**
   * Draw google map into an iframe
   * Intended to use on web page
   * @param {Object} options
   * @param {Function} onCompleted
   */
  drawToIFrame(options, onCompleted) {
    const currentIframe = this.chartContainer.getElementsByTagName('iframe')[0];
    if (!!currentIframe) {
      this.clearIFrame(currentIframe);
    }
    const iframe = document.createElement('iframe');
    iframe.setAttribute('src', this.getEmptyPageUrl());
    iframe.setAttribute('style', 'width: 100%; height: 100%; border: none;');
    const host = this;
    const { googleApiUrl, izendaUICssLink } = host;

    iframe.onload = function() {
      const doc = iframe.contentDocument;
      doc.getElementsByTagName('body')[0].style.margin = 0;

      const googleMapScript = document.createElement('script');
      googleMapScript.type = 'text/javascript';
      googleMapScript.src = `${googleApiUrl}&callback=showMap`;
      doc.querySelector('body').appendChild(googleMapScript);

      const izendaStyle = document.createElement('link');
      izendaStyle.rel = 'stylesheet';
      izendaStyle.href = izendaUICssLink;
      doc.getElementsByTagName('head')[0].appendChild(izendaStyle);

      const izendaContainer = doc.createElement('div');
      izendaContainer.setAttribute('style', 'width: 100%; height: 100%');
      izendaContainer.className = 'container';

      const izendaRoot = doc.createElement('div');
      izendaRoot.className = 'izenda-root izenda-container';

      const izendaPage = doc.createElement('section');
      izendaPage.className = 'izenda page u_overflowHidden';
      izendaRoot.appendChild(izendaPage);

      const chartBody = doc.createElement('div');
      chartBody.className = 'izenda-Wrapper-content';
      chartBody.setAttribute('style', 'width: 100%; height: 100%');

      izendaPage.appendChild(chartBody);
      izendaContainer.appendChild(izendaRoot);
      doc.body.appendChild(izendaContainer);

      iframe.contentWindow.showMap = function() {
        host.drawProcess.call(this, host, chartBody, options, onCompleted);
      };

      // callback when google api key is invalid
      this.contentWindow.gm_authFailure = host.onAuthFailure.bind(host);
    };
    this.chartContainer.appendChild(iframe);
    this.iframe = iframe;
  }

  /**
   * Clear iframe object
   * @param {Object} iframe
   */
  clearIFrame(iframe: any) {
    if (iframe instanceof HTMLIFrameElement) {
      iframe.remove();
    }
  }

  /**
   * Get google maps page url
   */
  getEmptyPageUrl() {
    let gmPageUrl = izendaCfg.RootPath || '/';
    if(gmPageUrl.substr(-1) != '/') gmPageUrl += '/'; 
    gmPageUrl += GMPAGE;
    return gmPageUrl;
  }

  /**
   * Resolve izenda-ui.css link
   */
  resolveIzendaUICSS() {
    const izendaUICssRegex = /.*\/izenda-ui\.css/;
    const matchedLink = find(l => l.href.match(izendaUICssRegex), document.getElementsByTagName('link')) || {};

    return matchedLink.href;
  }

  /**
   * Handle on failed authenticate/authorize google maps
   */
  onAuthFailure() {
    this.drawingService.drawFailing(this.chartContainer);
    this.shouldRender = false;
  }

  /**
   * Set google map options
   * @param {DOMNode} chartContainer
   * @param {Object} options
   */
  createCustomMapOptions(chartContainer, options) {
    const {
      mapOptions,
      mapOptions: { zoomControl },
      zoomControlOptions: { position },
      printingMode
    } = options;

    let googleMapsMapOptions = mapOptions;
    const containerWidth = chartContainer.clientWidth;

    if (!isNil(mapOptions.zoom)) {
      googleMapsMapOptions.zoom = mapOptions.zoom;
    } else {
      // Calculate appropriate zoom level and update to part content
      googleMapsMapOptions.zoom = Math.round(Math.log2(containerWidth / GOOGLE_MAP_ZOOM_SIZE_TILE));
      const { changeZoomLevel } = options;
      changeZoomLevel && changeZoomLevel(googleMapsMapOptions.zoom);
    }

    // Disable zoom control when printing
    printingMode && (googleMapsMapOptions.zoomControl = false);

    if (zoomControl) {
      googleMapsMapOptions = Object.assign(
        {
          zoomControlOptions: {
            position: this.google.maps.ControlPosition[position]
          }
        },
        mapOptions
      );
    }

    return googleMapsMapOptions;
  }

  /**
   * Refresh map
   * - by legend changing
   * @param {Object} changing series
   */
  refresh({ key, nextDisplayStatus }) {
    const selectedSeries = this.map.izItems.get(key);
    selectedSeries.forEach(i => i.setMap(nextDisplayStatus ? this.map : null));
    const selectedControls = find({ key }, this.map.izMapLabels);

    if (selectedControls) {
      selectedControls.display = nextDisplayStatus;
      selectedControls.items.forEach(i => (nextDisplayStatus ? i.open() : i.close()));
    }

    showOnlyOneLabel(this.map.izMapLabels);
  }

  /**
   * After google map ready
   * @param {Function} onCompleted
   */
  finish(onCompleted, onCompletedDrawLegendPromise: Promise) {
    const onCompletedTimeout = onCompleted =>
      setTimeout(() => {
        onCompletedDrawLegendPromise.then(() => onCompleted && onCompleted());
      }, 0);
    let timeoutDisposer = null;
    try {
      this.google.maps.event.addListenerOnce(this.map, onCompletedEventName, () => {
        timeoutDisposer = onCompletedTimeout(onCompleted);
      });
    } catch (error) {
      timeoutDisposer = onCompletedTimeout(onCompleted);
    } finally {
      timeoutDisposer && timeoutDisposer();
    }
  }

  /**
   * Drawing map and its elements
   * @param {Object} host Parent of container's google map. Most use in iframe case
   * @param {DOMNode} chartBody Element contains map
   * @param {Object} options
   * @param {Function} onCompleted
   */
  drawProcess(host, chartBody, options, onCompleted) {
    const customMapOptions = host.createCustomMapOptions.call(this, chartBody, options);
    // Merge custom options with default options
    const mapOptions = Object.assign(ReportPartMapConstants.GOOGLE_MAPS_DEFAULT_OPTION, customMapOptions);
    this.map = new this.google.maps.Map(chartBody, mapOptions);

    this.map.izItems = new Map(); // collection of main items on map: bubble, shading
    this.map.izMapLabels = []; // collection of label items on map

    this.map.refresh = (...args) => host.refresh.call(this, ...args);

    host.drawingService.setMap(this.google, this.map, options);

    let resolveDrawLegend = null;
    const onCompletedDrawLegendPromise = new Promise(resolve => (resolveDrawLegend = resolve));

    this.map.izShowGradientCaret = host.drawingService.drawLegend(options, resolveDrawLegend);
    host.drawingService.drawShading(options);
    host.drawingService.drawBubble(options);

    this.map.izItems.forEach(series => series.forEach(i => i.setMap(this.map)));

    showOnlyOneLabel(this.map.izMapLabels);
    host.finish.call(this, onCompleted, onCompletedDrawLegendPromise, options);
  }
}

/**
 * Show only one label of duplicate place
 * @param {Array} izMapLabels
 */
function showOnlyOneLabel(izMapLabels: Array) {
  const shadingLabels = find({ seriesType: 'shadingSeries' }, izMapLabels);
  const bubbleLabels = filter({ seriesType: 'bubbleSeries' }, izMapLabels);
  // Prioritize shading labels over bubble labels
  if (shadingLabels && shadingLabels.display) {
    const allShadingLabelsPos = shadingLabels.items.map(ifw => ({
      lat: ifw._marker.position.lat(),
      lng: ifw._marker.position.lng()
    }));

    // Only turn off duplicate label on the same position
    bubbleLabels.forEach(group =>
      group.items.forEach(infoWindow => {
        const position = { lat: infoWindow._marker.position.lat(), lng: infoWindow._marker.position.lng() };
        if (find({ lat: position.lat, lng: position.lng }, allShadingLabelsPos)) {
          infoWindow.close();
        }
      })
    );
  } else {
    // Only show one group bubble label and is the first display bubble series
    const displayBubbleLabels = find({ display: true }, bubbleLabels);
    if (displayBubbleLabels) {
      displayBubbleLabels.items.forEach(infoWindow => infoWindow.open());
    }
  }
}



// WEBPACK FOOTER //
// ./ui/share/components/charting/googlemaps/Izenda.GoogleMapsVizEngine.js